import React from 'react';
import { useNavigate } from 'react-router-dom';

const css = `
.sc-beta {
  position: sticky; top: 0; z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  min-height: 44px; padding: 6px 16px; box-sizing: border-box;
  background: #f2f2f2ff; border-bottom: 1px solid #F5D3BB;
  font-family: Inter, system-ui, -apple-system, sans-serif; font-size: 14px; line-height: 1.4;
}
/* Content stays grouped in the middle instead of spreading edge to edge */
.sc-beta__inner {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  width: auto; max-width: 900px; margin: 0 auto;
}
.sc-beta__pill {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  padding: 4px 10px; border-radius: 999px; background: #121A2C; color:  #e7dfdaff;
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
}
.sc-beta__msg { margin: 0; color: #2A3145; }
.sc-beta__msg--desktop { flex: 0 1 auto; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc-beta__msg--mobile { display: none; }
.sc-beta__link {
  display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  color: #f3722cff; font-weight: 600; text-decoration: none; cursor: pointer;
}
.sc-beta__link:hover { color: #8A3508; }
.sc-beta__link:focus-visible { outline: 2px solid #B4470C; outline-offset: 2px; border-radius: 6px; }

@media (max-width: 760px) {
  .sc-beta { justify-content: center; min-height: 52px; padding: 8px 24px; font-size: 13px; line-height: 1.35; }
  /* One row: BETA pill on the left, 2-line message on the right — whole group centered */
  .sc-beta__inner { flex-direction: row; align-items: center; justify-content: center; gap: 10px; width: auto; max-width: 340px; margin: 0 auto; }
  .sc-beta__pill { font-size: 10px; padding: 4px 8px; gap: 5px; }
  .sc-beta__msg--desktop, .sc-beta__link--desktop { display: none; }
  .sc-beta__msg--mobile { display: block; flex: 0 1 auto; text-align: left; }
  .sc-beta__msg--mobile .sc-beta__link { display: inline; text-decoration: underline; text-underline-offset: 2px; }
}
`;

const FlaskIcon = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.4'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M9 3h6' />
    <path d='M10 3v6l-5.5 9.5A2 2 0 0 0 6.2 21.5h11.6a2 2 0 0 0 1.7-3L14 9V3' />
    <path d='M7.5 15h9' />
  </svg>
);

const BugIcon = () => (
  <svg
    width='15'
    height='15'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M8 10h8v5a4 4 0 0 1-8 0z' />
    <path d='M9.5 10V8a2.5 2.5 0 0 1 5 0v2' />
    <path d='M4 14h4M16 14h4M5 9l3 2M19 9l-3 2M5 20l3-2M19 20l-3-2' />
  </svg>
);

/**
 * Light beta banner — always visible (no close button).
 *
 * Props:
 *  - reportPath: route of the bug report page (default "/report-bug")
 */
export default function BetaBanner({ reportPath = '/report-bug' }) {
  const navigate = useNavigate();

  const RedirectReportBug = (e) => {
    e.preventDefault(); // keep the href for right-click / open in new tab
    navigate(reportPath);
  };

  return (
    <>
      <style>{css}</style>
      <section className='sc-beta' aria-label='Beta notice'>
        <div className='sc-beta__inner'>
          <span className='sc-beta__pill'>
            <FlaskIcon />
            BETA
          </span>

          {/* Desktop — one line */}
          <p className='sc-beta__msg sc-beta__msg--desktop'>
            We're in early beta — sorry for any hiccups. Spotted a bug? Help us improve.
          </p>
          <a
            className='sc-beta__link sc-beta__link--desktop'
            href={reportPath}
            onClick={RedirectReportBug}
          >
            <BugIcon />
            Report a bug
          </a>

          {/* Mobile */}
          <p className='sc-beta__msg sc-beta__msg--mobile'>
            We're in beta — sorry for any bumps! Spotted an issue?{' '}
            <a className='sc-beta__link' href={reportPath} onClick={RedirectReportBug}>
              Report a bug
            </a>
          </p>
        </div>
      </section>
    </>
  );
}
